/**
 * Exact decimal handling for the supplier invoice form (batch 8 T12c3, #3033, NUMERIC-PRECISION.md).
 *
 * The user types euros; the API takes integer cents for totals, shipping and stated line nets and a unit price in cents with up to
 * four decimals (`1000.5` = 10,005 euros at four-decimal cent precision). Nothing here goes through a float on the way in: the text
 * is parsed to a BigInt scaled integer, and the JSON number is produced from the exact decimal text (a number whose shortest decimal
 * form is that text; anything else is refused). `Math.round(euros * 100)` is never used. Nothing here applies a financial rule: no
 * total, VAT or rounding is computed, only the unit conversion of what the user typed and the exact display of what the server said.
 */

export type DecimalResult<T> = { readonly ok: true; readonly value: T } | { readonly ok: false; readonly error: 'format' | 'decimals' | 'range' };

const INT_MAX = 2_147_483_647;
export const MAX_UNIT_PRICE_CENTS_TEXT = '99999999.9999';

interface Scaled {
  readonly negative: boolean;
  /** Integer digits and fraction digits, the fraction trimmed of trailing zeros. */
  readonly whole: string;
  readonly fraction: string;
}

/** `12,50`, `12.5`, `-1 234,5`, `+3`. One decimal separator; spaces (also no-break) group thousands. No exponent, no hex. */
function parseText(text: string): Scaled | null {
  const compact = text.replace(/\s/g, '');
  const match = /^([+-])?(\d+)(?:[.,](\d*))?$/.exec(compact);
  if (!match) return null;
  const whole = (match[2] as string).replace(/^0+(?=\d)/, '');
  const fraction = (match[3] ?? '').replace(/0+$/, '');
  return { negative: match[1] === '-', whole, fraction };
}

/** `text` as a BigInt scaled by 10^decimals, refusing digits that would be lost. */
export function parseScaled(text: string, decimals: number): DecimalResult<bigint> {
  const parsed = parseText(text);
  if (!parsed) return { ok: false, error: 'format' };
  if (parsed.fraction.length > decimals) return { ok: false, error: 'decimals' };
  const magnitude = BigInt(`${parsed.whole}${parsed.fraction.padEnd(decimals, '0')}`);
  return { ok: true, value: parsed.negative ? -magnitude : magnitude };
}

const isSafe = (value: bigint): boolean => value <= BigInt(Number.MAX_SAFE_INTEGER) && value >= -BigInt(Number.MAX_SAFE_INTEGER);

/** Euros to whole cents (the API's integer money): at most two decimals, within the INTEGER column. */
export function eurosToCents(text: string): DecimalResult<number> {
  const scaled = parseScaled(text, 2);
  if (!scaled.ok) return scaled;
  if (!isSafe(scaled.value)) return { ok: false, error: 'range' };
  const value = Number(scaled.value);
  if (Math.abs(value) > INT_MAX) return { ok: false, error: 'range' };
  return { ok: true, value: value === 0 ? 0 : value };
}

/** `digits` (an integer, possibly negative) with the decimal point moved `places` to the left, as exact decimal text. */
function shiftLeft(digits: bigint, places: number): string {
  const negative = digits < 0n;
  const padded = (negative ? -digits : digits).toString().padStart(places + 1, '0');
  const whole = padded.slice(0, padded.length - places);
  const fraction = padded.slice(padded.length - places).replace(/0+$/, '');
  return `${negative ? '-' : ''}${whole}${fraction === '' ? '' : `.${fraction}`}`;
}

/**
 * Euros to cents at four decimals (the unit price): up to six decimals of a euro, non-negative, at most 99,999,999.9999 cents.
 * The returned JSON number is exactly the typed value: its shortest decimal form equals the exact text, else it is refused.
 */
export function eurosToUnitCents(text: string): DecimalResult<number> {
  const scaled = parseScaled(text, 6);
  if (!scaled.ok) return scaled;
  if (scaled.value < 0n || scaled.value > 999_999_999_999n) return { ok: false, error: 'range' };
  // euros x 10^6 is cents x 10^4.
  const centsText = shiftLeft(scaled.value, 4);
  const value = Number(centsText);
  if (String(value) !== centsText) return { ok: false, error: 'range' };
  return { ok: true, value };
}

/** A percent (VAT, discount) with at most four decimals within 0..100, as the exact JSON number. */
export function parsePercent(text: string): DecimalResult<number> {
  const scaled = parseScaled(text, 4);
  if (!scaled.ok) return scaled;
  if (scaled.value < 0n || scaled.value > 1_000_000n) return { ok: false, error: 'range' };
  const normalized = shiftLeft(scaled.value, 4);
  const value = Number(normalized);
  if (String(value) !== normalized) return { ok: false, error: 'range' };
  return { ok: true, value };
}

/** A signed whole number (a quantity); `max` is the API bound. */
export function parseSignedInteger(text: string, max: number): DecimalResult<number> {
  const compact = text.replace(/\s/g, '');
  if (!/^[+-]?\d+$/.test(compact)) return { ok: false, error: 'format' };
  const value = Number(compact);
  if (!Number.isSafeInteger(value)) return { ok: false, error: 'range' };
  if (Math.abs(value) > max) return { ok: false, error: 'range' };
  return { ok: true, value: value === 0 ? 0 : value };
}

// ---------------------------------------------------------------------------
// Display of server values (exact, no float division)
// ---------------------------------------------------------------------------

const groupWhole = (whole: string, locale: string): string => {
  try {
    return new Intl.NumberFormat(locale, { useGrouping: true }).format(BigInt(whole));
  } catch {
    return whole;
  }
};
const decimalSeparator = (locale: string): string => {
  try {
    return new Intl.NumberFormat(locale).formatToParts(1.1).find((part) => part.type === 'decimal')?.value ?? '.';
  } catch {
    return '.';
  }
};

/** Exact decimal text (`-12.345`) in the user's locale with at least `minDecimals` decimals. */
function localizeDecimal(text: string, locale: string, minDecimals: number): string {
  const negative = text.startsWith('-');
  const [whole = '0', fraction = ''] = (negative ? text.slice(1) : text).split('.');
  const padded = fraction.padEnd(minDecimals, '0');
  return `${negative ? '-' : ''}${groupWhole(whole, locale)}${padded === '' ? '' : `${decimalSeparator(locale)}${padded}`}`;
}

/** Whole cents (an integer from the server) as euros. */
export function formatCents(cents: number, locale: string): string {
  if (!Number.isSafeInteger(cents)) return String(cents);
  return `${localizeDecimal(shiftLeft(BigInt(cents), 2), locale, 2)}\u00a0€`;
}

/** A cents value with up to four decimals (a unit price) as euros with up to six decimals, never fewer than two. */
export function formatUnitCents(cents: number, locale: string): string {
  const text = String(cents);
  const match = /^(-)?(\d+)(?:\.(\d{1,4}))?$/.exec(text);
  if (!match) return text;
  const fraction = (match[3] ?? '').padEnd(4, '0');
  const scaled = BigInt(`${match[2] as string}${fraction}`);
  const euros = shiftLeft(match[1] ? -scaled : scaled, 6);
  return `${localizeDecimal(euros, locale, 2)}\u00a0€`;
}

/** A percent from the server (at most four decimals). */
export function formatPercent(percent: number, locale: string): string {
  const text = String(percent);
  if (!/^-?\d+(\.\d+)?$/.test(text)) return text;
  return `${localizeDecimal(text, locale, 0)}\u00a0%`;
}
